/* eslint-disable no-underscore-dangle, no-case-declarations */
import { useLang } from "@hooks/useLang";
import { useMemo, useRef, useState } from "react";
import {
  ANALYTICS_DEFAULT_DATE_TIME,
  FilterType,
  getDefaultChartOrder,
  ObjectType
} from "@queries/tanstack/analytics-filter/AnalyticsFilterQuery";
import { CalendarModeRangeType } from "@custom-types/Types";
import _ from "lodash";
import { FlatNodeItem } from "@components/common/tree-view/Interfaces";
import { HVButton, useHVSnackbar } from "@repo/HVComponents";
import moment from "moment";
import {
  FilterListAccordionButton,
  MORE_TYPE
} from "./btns/accordion/FilterListAccordionButton";
import * as S from "./LeftMenu.styled";
import { SaveButton } from "./btns/SaveButton";
import { CalendarButton } from "./btns/CalendarButton";
import { RuleButton } from "./btns/RuleButton";
import { ObjectTypeButton } from "./btns/ObjectTypeButton";

interface LeftMenuProps {
  savedFilters: FilterType[];
  savedFilterIdx: number;
  searchedFilter?: FilterType;
  onSearch: (filter: FilterType) => void;
  onSave: (
    type: "SAVE" | "SAVE_AS",
    options: { fileName: string; id?: string }
  ) => void;
  onRemove: (id: string) => void;
  onChangeTab: (id: string) => void;
  onChangeFilterName: (id: string, filterName: string) => void;
}

const DEFAULT_FILTER: FilterType = {
  filterName: "FilterName",
  order: [],
  rules: [],
  baseTime: ANALYTICS_DEFAULT_DATE_TIME,
  objectType: "People"
};

export const LeftMenu = (props: LeftMenuProps) => {
  const { translate } = useLang();
  const selectedFilter =
    Array.isArray(props.savedFilters) &&
    props.savedFilters[props.savedFilterIdx]
      ? props.savedFilters[props.savedFilterIdx]
      : _.cloneDeep(DEFAULT_FILTER);

  const selectedId = selectedFilter._id;

  const [tempSavedFilter, setTempSavedFilter] =
    useState<FilterType>(selectedFilter);

  const selectedFilterId = props.savedFilters[props.savedFilterIdx]?._id;
  const prevTempSavedFilter = useRef<FilterType>(tempSavedFilter);
  const { showSnackbar } = useHVSnackbar();
  const [isSearched, setIsSearched] = useState(false);

  const handleSearch = () => {
    if (tempSavedFilter) {
      if (_.isEqual(prevTempSavedFilter.current, tempSavedFilter)) {
        return;
      }

      const newTempSavedFilter = {
        ...tempSavedFilter,
        order: getDefaultChartOrder(tempSavedFilter.rules)
      };

      prevTempSavedFilter.current = _.cloneDeep(newTempSavedFilter);
      props.onSearch(newTempSavedFilter);
      setTempSavedFilter(newTempSavedFilter);
      setIsSearched(true);
    }
  };

  const handleMore = (moreType: MORE_TYPE, id: string) => {
    switch (moreType) {
      case "REMOVE_FILTER":
        if (id) {
          if (selectedId !== id) {
            const filter =
              _.cloneDeep(
                props.savedFilters.find((item) => item._id === selectedId)
              ) ?? DEFAULT_FILTER;
            setTempSavedFilter(filter);
          } else {
            const filtered = props.savedFilters.filter(
              (item) => item._id !== id
            );
            if (filtered.length > 0) {
              setTempSavedFilter(_.cloneDeep(filtered[0]));
            } else {
              setIsSearched(false);
              setTempSavedFilter(_.cloneDeep(DEFAULT_FILTER));
            }
          }
          props.onRemove(id);
        }
        break;
      default:
        break;
    }
  };

  const disableSearch = (() => {
    const isNotSearched = isSearched && !props.searchedFilter;
    if (isNotSearched) {
      return true;
    }

    const isAllCategorySelected =
      !tempSavedFilter?.baseTime ||
      !tempSavedFilter?.rules ||
      tempSavedFilter?.rules.length === 0 ||
      !tempSavedFilter?.objectType;
    if (isAllCategorySelected) {
      return true;
    }

    const isNotChangedDate =
      !!props.searchedFilter?.baseTime &&
      !!tempSavedFilter?.baseTime &&
      moment(props.searchedFilter.baseTime.start).isSame(
        moment(tempSavedFilter.baseTime.start)
      ) &&
      moment(props.searchedFilter.baseTime.end).isSame(
        moment(tempSavedFilter.baseTime.end)
      );

    if (!isNotChangedDate) {
      return false;
    }

    const isNotChangedOther =
      props.searchedFilter &&
      _.isEqual(
        _.pick(props.searchedFilter, ["rules", "objectType"]),
        _.pick(tempSavedFilter, ["rules", "objectType"])
      );

    if (!isNotChangedOther) {
      return false;
    }

    return true;
  })();

  const disableSave = (() => {
    if (!isSearched) {
      // if not searched, but changed order
      const isChangedOrder =
        !!props.savedFilters &&
        !!props.searchedFilter &&
        !_.isEqual(
          props.savedFilters[props.savedFilterIdx]?.order,
          props.searchedFilter.order
        );

      if (isChangedOrder) {
        return false;
      }
      return true;
    }

    return false;
  })();

  const filterList = useMemo(() => {
    if (props.savedFilters.length > 0) {
      return props.savedFilters.map((filter) => ({
        value: filter._id ?? "",
        label: filter.filterName
      }));
    }

    return undefined;
  }, [props.savedFilters]);

  const isValidName = (name: string) => {
    return !_.isEmpty(name.trim());
  };

  const handleSave = (
    status: "SAVE" | "SAVE_AS",
    options: {
      fileName?: string;
    }
  ) => {
    const fileName =
      options.fileName || props.savedFilters[props.savedFilterIdx].filterName;
    if (fileName && isValidName(fileName)) {
      // change filter name ( save as, change filter name )
      props.onSave(status, { fileName, id: selectedFilterId });
      setTempSavedFilter((prev) => ({ ...prev, filterName: fileName }));
      setIsSearched(false);
    } else {
      showSnackbar({
        id: "enter-right-name",
        message: translate("analytics_enter_right_name"),
        variant: "fail"
      });
    }
  };

  const handleChangeName = (name: string, id: string) => {
    if (id) {
      if (isValidName(name)) {
        props.onChangeFilterName(id, name);

        if (id === selectedId) {
          setTempSavedFilter((prev) => ({ ...prev, name }));
        }
      } else {
        showSnackbar({
          id: "enter-right-name",
          message: translate("analytics_enter_right_name"),
          variant: "fail"
        });
      }
    } else {
      showSnackbar({
        id: "filter-id-not-found",
        message: "filter id not found",
        variant: "fail"
      });
    }
  };

  const handleChangeTab = (id: string) => {
    const tab =
      props.savedFilters.find((item) => item._id === id) ??
      _.cloneDeep(DEFAULT_FILTER);
    setTempSavedFilter(tab);
    props.onChangeTab(id);
  };

  const handleChangeDate = (v: {
    start: string;
    end: string;
    mode: CalendarModeRangeType;
  }) => {
    setTempSavedFilter((prev) => ({
      ...(prev ?? {}),
      baseTime: v
    }));
  };

  const handleChangeRule = (v: FlatNodeItem[]) => {
    const rules = _.map(v, (rule) => rule.value) as string[];
    setTempSavedFilter((prev) => ({
      ...(prev ?? {}),
      rules
    }));
  };

  const handleChangeObjectType = (objectType: ObjectType) => {
    setTempSavedFilter((prev) => ({
      ...(prev ?? {}),
      rules: [],
      objectType
    }));
  };

  return (
    <S.Container>
      <S.Title>{translate("analytics")}</S.Title>
      <S.FilterWrap>
        <S.FilterName key={`${selectedId}_filterName`}>
          {translate("save_filter")}
          <div style={{ display: "flex", alignItems: "center" }}>
            <SaveButton
              savedCount={props.savedFilters.length}
              disabled={disableSave}
              onSave={handleSave}
            />
          </div>
        </S.FilterName>
        <div>
          <FilterListAccordionButton
            // key={String(props.savedFilterIdx)}
            disableMore={!filterList}
            defaultId={selectedFilterId}
            list={
              filterList ?? [
                {
                  value: "Analytics list",
                  label: "Analytics list"
                }
              ]
            }
            onChangeFilter={handleChangeTab}
            onChangeMoreType={handleMore}
            onChangeFiltereName={handleChangeName}
          />
        </div>
      </S.FilterWrap>
      <S.Divider />
      <S.FilterWrap>
        <S.FilterName> {translate("period")}</S.FilterName>
        <div>
          <CalendarButton
            key={`${selectedId}_period_${props.savedFilters[
              props.savedFilterIdx
            ]?.baseTime?.start}-${props.savedFilters[props.savedFilterIdx]
              ?.baseTime?.end}-${props.savedFilters[props.savedFilterIdx]
              ?.baseTime?.mode}`}
            dateTime={tempSavedFilter?.baseTime}
            onChange={handleChangeDate}
          />
        </div>
      </S.FilterWrap>
      <S.FilterWrap key={`${selectedId}_objectType`}>
        <S.FilterName>{translate("object_type")}</S.FilterName>
        <ObjectTypeButton
          defaultType={tempSavedFilter.objectType}
          onChange={handleChangeObjectType}
        />
      </S.FilterWrap>
      <S.FilterWrap key={`${selectedId}_location`}>
        <S.FilterName>{translate("locations")}</S.FilterName>
        <RuleButton
          key={`${tempSavedFilter.objectType}_${props.savedFilterIdx}`}
          ruleCategory={tempSavedFilter.objectType}
          defaultRules={tempSavedFilter.rules ?? []}
          onChange={handleChangeRule}
        />
      </S.FilterWrap>
      <S.BottomWrap key={`${selectedId}_search`}>
        <HVButton
          onClick={handleSearch}
          buttonContainerProps={{
            disabled: disableSearch,
            w: "100%",
            style: { width: "100%", height: "32px", padding: "0px 16px" }
          }}
        >
          {translate("search")}
        </HVButton>
      </S.BottomWrap>
    </S.Container>
  );
};
